import { audioFileUrl, type Narrator } from "@serophin/nottldr-shared";
import { useEffect, useRef, useState } from "preact/hooks";
import { useToast } from "../../contexts/ToastContext";

export type NarratorSample = {
  playing: Narrator | null;
  loading: Narrator | null;
  /** Plays the narrator's sample, or stops it when it is the one playing. */
  toggle: (narrator: Narrator) => void;
};

type SampleState = { playing: Narrator | null; loading: Narrator | null };

const IDLE: SampleState = { playing: null, loading: null };

type Handlers = { onReady: () => void; onEnded: () => void; onFail: (message: string) => void };

const play = (narrator: Narrator, handlers: Handlers): HTMLAudioElement => {
  const audio = new Audio(audioFileUrl(`${narrator}.sample.m4a`));
  audio.addEventListener("canplay", handlers.onReady);
  audio.addEventListener("ended", handlers.onEnded);
  audio.addEventListener("error", () => handlers.onFail("Failed to load audio sample"));
  audio.play().catch(() => handlers.onFail("Failed to play audio sample"));
  return audio;
};

const silence = (audio: HTMLAudioElement | null): void => {
  audio?.pause();
  audio?.removeAttribute("src");
  audio?.load();
};

/** Plays `<narrator>.sample.m4a` from the audio host with a plain audio element; one sample at a time, stopped when the panel goes. */
export const useNarratorSample = (): NarratorSample => {
  const toast = useToast();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [state, setState] = useState<SampleState>(IDLE);

  const stop = (error?: string) => {
    silence(audioRef.current);
    audioRef.current = null;
    setState(IDLE);
    if (error) {
      toast.error(error);
    }
  };

  useEffect(() => stop, []);

  const start = (narrator: Narrator) => {
    // An event of a sample that has been replaced or stopped is ignored.
    const live = (run: () => void) => () => audioRef.current === audio && run();
    const audio = play(narrator, {
      onReady: live(() => setState({ playing: narrator, loading: null })),
      onEnded: live(() => stop()),
      onFail: (message) => live(() => stop(message))(),
    });
    audioRef.current = audio;
    setState({ playing: null, loading: narrator });
  };

  const toggle = (narrator: Narrator) => {
    const wasPlaying = state.playing === narrator;
    stop();
    if (!wasPlaying) {
      start(narrator);
    }
  };

  return { ...state, toggle };
};
